import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ActivityIndicator,
  Linking,
  Platform,
  Share,
  StyleSheet,
} from 'react-native'

import { publishEvent } from '@zet/analytics'
import KeyBoardWrapper from '@zet/common/components/keyboardWrapper'
import LazyHOC from '@zet/common/components/lazyHoc'
import { useToast } from '@zet/common/components/toast'
import { useModal } from '@zet/common/hooks/useModal'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import { colors } from '@zet/design-system'
import { Alert } from '@zet/design-system/alert'
import { Button } from '@zet/design-system/button'
import { Checkbox } from '@zet/design-system/checkbox'
import { Icon } from '@zet/design-system/icons'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import {
  B1,
  B2,
  B5,
  H5,
  H6,
  SH2,
  SH3,
  Text,
} from '@zet/design-system/typography'
import { APP_NAME } from '@zet/end-points'
import { useExperiments } from '@zet/experiments'
import { useWebShare } from '@zet/feature-brands/hooks/useWebShare'
import { useUserContext } from '@zet/feature-user/api/user/hooks'
import { i18n } from '@zet/localization'
import { zetApi } from '@zet/network'
import { OTP_REGEX } from '@zet/utils/regularExpression'
import LottieView from 'lottie-react-native'
import { styled } from 'nativewind'
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from 'react-native-confirmation-code-field'

import { IRequestData } from '../../api/functions/customerAdditionStateMachine'
import {
  useAddCustomerNextState,
  useShareOtpVerificationLink,
  useVerifyPhoneNumber,
} from '../../api/hooks'
import {
  useSendConsentOtp,
  useShareReconsentOtpVerificationLink,
  useVerifyConsentOtp,
} from '../../api/hooks/reconsent'
import AddCustomerBackHandlerHOC from '../../components/addCustomerBackHandlerHOC'
import useStore from '../../store'

const { locale = 'en' } = zetApi.defaults.headers.common

const BoldText = styled(
  Text,
  'text-primary-100 font-[Graphie-Bold] text-sm leading-5',
)

const VerifyCustomerOtp = ({
  route,
}: {
  route: {
    params: {
      customerId: number | undefined
      isReconsentFlow: boolean
      isDemoSale?: boolean
      subagentId?: number
      isExperianEnabled: boolean
    }
  }
}) => {
  const {
    params: {
      customerId,
      subagentId,
      isReconsentFlow = false,
      isDemoSale = false,
      isExperianEnabled,
    },
  } = route

  const { data } = useExperiments()
  const { WebShare, openWebShare } = useWebShare()
  const [value, setValue] = useState('')

  const [isOtpVerifyViaWeb, setIsOtpVerifyViaWeb] = useState(false)
  const ref = useBlurOnFulfill({ value, cellCount: 4 })
  const [isShared, setIsShared] = useState<boolean>(false)
  const [disableResend, setDisableResend] = useState(false)
  const [props, getCellOnLayoutHandler] = useClearByFocusCell({
    value,
    setValue,
  })
  const [isConsentGiven, setIsConsentGiven] = useState<boolean>(false)

  const { selectedCustomer } = useStore((state) => state)
  const [timer, setTimer] = useState<number>(30)
  const { openModal } = useModal()
  const navigation = useNavigation()
  const toast = useToast()
  const { mutate: sendConsentOtpMutate } = useSendConsentOtp()
  const {
    data: consentData,
    mutate: consentMutate,
    isSuccess: consentIsSuccess,
    isLoading: consentIsLoading,
  } = useVerifyConsentOtp()

  const { data: user } = useUserContext()

  const { mutate, isLoading: isExperimentsLoading } = useAddCustomerNextState()
  const handleSubmit = () => {
    const requestBodyData = {
      customerId: customerId as number,
      otp: isShared ? '' : value,
      phoneNumber: selectedCustomer?.phoneNumber as string,
      // ...(data?.UI_isAddCustomerNewFlow
      //   ? {
      screenName: isOtpVerifyViaWeb ? 'GET_CURRENT_STATE' : 'SHOWN_OTP_SCREEN',
      // }
      // : {}),
      isDemoSale: isDemoSale,
    }
    // if (data?.UI_isAddCustomerNewFlow) {
    mutate(requestBodyData as IRequestData)
    // }
  }

  const handleButtonDisablity = () => {
    if (isShared && isConsentGiven) return false
    else if (isExperianEnabled && !isConsentGiven) return true
    else if (value.length === 4 && OTP_REGEX.test(value)) return false
    else return true
  }

  const onShare = useCallback((response) => {
    clearInterval(timerRef.current)
    ;(async () => {
      publishEvent('click_action', {
        item_name: 'add_customer_verify_otp_share',
      })
      if (Platform.OS === 'web') {
        await openWebShare({
          text: response?.data?.link || '',
        })
      } else {
        await Share.share({
          message: i18n.t('otpInput.shareDescription', {
            link: response?.data?.link,
            name: user?.fullName,
          }),
        })
      }
      setIsOtpVerifyViaWeb(true)
      setIsShared(true)
    })()
  }, [])

  const onReconsentShare = async (response) => {
    const result = await Share.share({
      message: i18n.t('otpInput.shareReconsentDescription', {
        link: response?.data?.link,
        name: user?.fullName,
      }),
    })
    setIsShared(true)
  }

  const { mutate: mutateVerifyPhoneNumber } = useVerifyPhoneNumber()
  const {
    data: shareOtpLinkResponse,
    refetch,
    isLoading: refetching,
    isFetching,
  } = useShareOtpVerificationLink({
    onSuccess: onShare,
    customerId,
  })

  const resetToOtpField = () => {
    setIsShared(false)
    timerFunction()
  }

  const {
    data: shareReconsentOtpLinkResponse,
    refetch: refetchReconsent,
    isLoading: refetchingReconsent,
    isFetching: isFetchingReconsent,
  } = useShareReconsentOtpVerificationLink({
    onSuccess: onReconsentShare,
    customerId,
  })

  //Timer
  const interval = 1000
  const timerRef = useRef(0)
  let prev = 0
  let diff = 0
  const timerCallback = () => {
    const date = new Date()
    setTimer((i) => {
      const current = date.getSeconds()
      if (diff === 0) {
        diff = 1
        prev = date.getSeconds()
        return 30
      }

      if (prev > current) diff = 60 - prev + current
      else diff = current - prev

      prev = date.getSeconds()

      if (i > 1)
        if (i + diff < 1) {
          clearInterval(timerRef.current)
          return 1
        } else {
          return i - diff
        }
      else {
        clearInterval(timerRef.current)
        return 0
      }
    })
  }
  const timerFunction = () => {
    timerRef.current = setInterval(timerCallback, interval)
  }

  const otpTimer = useCallback(timerFunction, [timerRef])

  useEffect(() => {
    otpTimer()
    if (isReconsentFlow) {
      publishEvent('screen_view', {
        screen_name: 'brands_list_recommendation_get_customer_consent',
      })
    }
    return () => {
      clearInterval(timerRef.current)
    }
  }, [])

  const handleConsentSubmit = () => {
    const requestBodyData = {
      customerId: customerId,
      otp: isShared ? '' : value,
      phoneNumber: selectedCustomer?.phoneNumber as string,
      // ...(data?.UI_isAddCustomerNewFlow
      //   ? {
      screenName: isOtpVerifyViaWeb ? 'GET_CURRENT_STATE' : 'SHOWN_OTP_SCREEN',
      isReconsentFlow,
      //   }
      // : {}),
      ...(subagentId ? { subagentId } : {}),
    }
    // if (data?.UI_isAddCustomerNewFlow) {
    mutate(requestBodyData as IRequestData)
    // } else {
    //   consentMutate({
    //     customerId: customerId as number,
    //     otp: isShared ? '' : value,
    //     phoneNumber: selectedCustomer?.phoneNumber as string,
    //   })
    // }
  }

  useEffect(() => {
    if (consentIsSuccess) {
      if (consentData?.data?.verified) {
        navigation.navigate('BrandPage', {
          customerId: customerId,
          customerName: selectedCustomer?.name,
          customerPhoneNumber: selectedCustomer?.phoneNumber,
          isBureauReportExpired: false,
        })
      } else if (
        consentData?.error?.code === 2025 &&
        consentData?.error?.message === 'Invalid phonenumber or otp'
      ) {
        toast?.show({
          intent: 'error',
          heading: consentData?.error?.message,
          hideAfter: 3000,
        })
      } else if (consentData?.error?.code === 3313) {
        toast?.show({
          intent: 'error',
          heading: consentData?.error?.message,
          hideAfter: 3000,
        })
      }
    }
  }, [consentIsSuccess])

  const resendOtp = () => {
    mutateVerifyPhoneNumber(
      {
        fullName: selectedCustomer?.name,
        phoneNumber: selectedCustomer?.phoneNumber,
      },
      {
        onSuccess: (data) => {
          navigation.setParams({
            isExperianEnabled: Boolean(data?.isExperianEnabled),
          })
          if (data.lockedTill) {
            setDisableResend(true)
          }
        },
      },
    )
  }

  const onPressResendButton = () => {
    prev = 0
    diff = 0
    timerFunction()
    if (isReconsentFlow) {
      sendConsentOtpMutate({
        fullName: selectedCustomer?.name,
        phoneNumber: selectedCustomer?.phoneNumber,
      })
    } else {
      resendOtp()
    }
  }

  const renderExperianConsents = () => {
    return (
      <XStack className="my-2 items-center">
        <Stack
          style={{
            flex: 0.1,
          }}
        >
          <Checkbox
            accesibilityLabel="consentToTnc"
            checked={isConsentGiven}
            onChange={setIsConsentGiven}
          />
        </Stack>
        <Stack
          style={{
            flex: 0.9,
          }}
        >
          <B2>
            {i18n.t('customerDetails.iAgree')}{' '}
            <BoldText
              onPress={() => {
                navigation.navigate('PrivacyPolicy', {
                  isCustomer: true,
                  isExperian: isExperianEnabled,
                })
                publishEvent('click_action', {
                  item_name: 'add_customer_privacy_policy',
                })
              }}
            >
              {i18n.t('verifyPhNum.privacyPolicy')}{' '}
            </BoldText>
            {i18n.t('customerDetails.and')}{' '}
            <BoldText
              onPress={() => {
                navigation.navigate('TermsOfUse', {
                  isCustomer: true,
                  isExperian: isExperianEnabled,
                })
                publishEvent('click_action', {
                  item_name: 'add_customer_terms_of_use',
                })
              }}
            >
              {i18n.t('customerDetails.terms')}{' '}
            </BoldText>
            {APP_NAME === MY_TEAMS_ADVISOR
              ? i18n.t('customerDetails.vistas')
              : i18n.t('customerDetails.zet')}
            <BoldText
              onPress={() => {
                navigation.navigate('ExperianTermsOfUse', {
                  isCustomer: true,
                })
                publishEvent('click_action', {
                  item_name: 'add_customer_experian_terms_and_conditions',
                })
              }}
            >
              {' '}
              {i18n.t('customerDetails.t&c')}{' '}
            </BoldText>
            {i18n.t('customerDetails.experian')}
          </B2>
        </Stack>
      </XStack>
    )
  }

  return (
    <LazyHOC>
      <AddCustomerBackHandlerHOC>
        <KeyBoardWrapper
          className="flex-1 bg-white "
          BottomSheet={
            <>
              {isReconsentFlow ? (
                <Stack className="border-t-[1px] border-[#ECEFF5] px-3 py-2">
                  {isExperianEnabled && renderExperianConsents()}
                  <Button
                    className="w-full rounded py-[12]"
                    disabled={handleButtonDisablity() || consentIsLoading}
                    onPress={handleConsentSubmit}
                    loading={consentIsLoading}
                    eventName="click_action"
                    eventPayload={{
                      item_name: 'add_customer_reconsent_verify_otp_continue',
                    }}
                  >
                    {i18n.t('reconsent.continue')}
                  </Button>
                </Stack>
              ) : (
                <>
                  <Stack className="border-t-[1px] border-[#ECEFF5] px-3 py-2">
                    {isExperianEnabled && renderExperianConsents()}
                    <Button
                      size="lg"
                      className={`w-full rounded py-[12] ${
                        handleButtonDisablity() ? 'bg-grays-50' : ''
                      }`}
                      disabled={handleButtonDisablity() || isExperimentsLoading}
                      onPress={handleSubmit}
                      buttonEvent={{
                        eventName: 'click_action',
                        payload: { item_name: 'add_customer_step_1_continue' },
                      }}
                      loading={isExperimentsLoading}
                      loaderText={i18n.t('customerDetails.verifying')}
                    >
                      {isReconsentFlow
                        ? i18n.t('reconsent.continue')
                        : i18n.t('customerDetails.verify')}
                    </Button>
                  </Stack>
                </>
              )}
            </>
          }
        >
          <Stack className="px-3">
            <Stack className="h-3" />
            <H5 className="text-grays-100">
              {isReconsentFlow
                ? i18n.t('customerDetails.consentHeader')
                : i18n.t('customerDetails.customerPermission')}
            </H5>

            {isShared ? (
              <>
                <Stack className="mt-2 h-14 w-14 self-center">
                  <LottieView
                    autoPlay
                    className=" h-[112] w-[88] self-center "
                    source={require('../../../assets/lottie/OTPVerification.json')}
                  />
                </Stack>

                <B1 className="text-grays-60 mt-20 ">
                  {i18n.t('customerDetails.sentVerificationLink')}

                  <H6 className=" text-grays-100">
                    {` ${selectedCustomer?.phoneNumber}. `}
                  </H6>

                  {i18n.t('customerDetails.askCustomer')}
                </B1>
                <XStack className=" mt-4  justify-between ">
                  <Button
                    onPress={() =>
                      Linking.openURL(`tel:${selectedCustomer?.phoneNumber}`)
                    }
                    textOnly={
                      !(APP_NAME === MY_TEAMS_ADVISOR && Platform.OS === 'web')
                    }
                    iconLeft={
                      <Icon
                        type="PhoneFilled"
                        color={colors.primary[50]}
                        className=" mr-2 "
                      />
                    }
                    className="px-0"
                    textStyle="text-primary-100"
                    eventName="click_action"
                    eventPayload={{
                      item_name: isReconsentFlow
                        ? 'brands_list_recommendation_reconsent_call_customer'
                        : 'add_customer_call_customer',
                    }}
                  >
                    {i18n.t('customerDetails.call')}
                  </Button>
                  <Button
                    onPress={() =>
                      isReconsentFlow
                        ? onReconsentShare(shareReconsentOtpLinkResponse)
                        : onShare(shareOtpLinkResponse)
                    }
                    textOnly={
                      !(APP_NAME === MY_TEAMS_ADVISOR && Platform.OS === 'web')
                    }
                    iconLeft={
                      <Icon
                        type="MessageFilled"
                        color={colors.primary[50]}
                        className=" mr-2 "
                      />
                    }
                    className="px-0"
                    textStyle="text-primary-100 "
                    eventName="click_action"
                    eventPayload={{
                      item_name: isReconsentFlow
                        ? 'brands_list_recommendation_reconsent_message_customer'
                        : 'add_customer_message_customer',
                    }}
                  >
                    {i18n.t('customerDetails.sendMessage')}
                  </Button>
                </XStack>
              </>
            ) : (
              <>
                <B1 className=" text-grays-60 mt-2">
                  {i18n.t('customerDetails.sentOtp')}{' '}
                  <H6 className="text-grays-100">
                    {selectedCustomer?.phoneNumber}.{' '}
                  </H6>
                  {i18n.t('customerDetails.fillOtp')}
                </B1>
                <Stack>
                  <CodeField
                    ref={ref}
                    {...props}
                    caretHidden={true}
                    value={value}
                    onChangeText={(i) => {
                      setValue(i)
                    }}
                    cellCount={4}
                    rootStyle={styles.codeFieldRoot}
                    keyboardType="number-pad"
                    textContentType="oneTimeCode"
                    renderCell={({ index, symbol, isFocused }) => (
                      <Stack
                        style={[styles.cell, isFocused && styles.focusCell]}
                      >
                        <Text
                          key={index}
                          onLayout={getCellOnLayoutHandler(index)}
                        >
                          {symbol || (isFocused ? <Cursor /> : null)}
                        </Text>
                      </Stack>
                    )}
                  />
                  {!OTP_REGEX.test(value) && (
                    <B2 className="text-error-100 mt-2 self-center">
                      Invalid OTP
                    </B2>
                  )}
                </Stack>
                <Stack className="mt-[20] items-center">
                  {timer === 0 || disableResend ? (
                    <Pressable
                      opacity={true}
                      onPress={onPressResendButton}
                      eventName="click_action"
                      eventPayload={{
                        item_name: isReconsentFlow
                          ? 'brands_list_recommendation_reconsent_resend_otp'
                          : 'add_customer_resend_otp',
                      }}
                    >
                      <SH3 className=" text-primary-100">
                        {i18n.t('customerDetails.resend')}
                      </SH3>
                    </Pressable>
                  ) : (
                    <B2 className=" text-grays-60 text-center">
                      {`Resend OTP in `}
                      <SH3 className="text-primary-100">
                        00:{timer < 10 ? `0${timer}` : timer}
                      </SH3>
                    </B2>
                  )}
                </Stack>
                {APP_NAME !== MY_TEAMS_ADVISOR && (
                  <Stack className=" mt-5 rounded-lg bg-[#ECFFEC] pb-2  ">
                    <Alert
                      intent="success"
                      iconLeft={{
                        name: 'SecureOutlined',
                        color: colors.success[150],
                      }}
                      className=" text-grays-60 "
                    >
                      {i18n.t('customerDetails.whyOtpRequired')}{' '}
                    </Alert>
                    <Button
                      onPress={() =>
                        openModal('YoutubeVideoModal', {
                          videoId: 'SGfT09xM1k0',
                          videoData: {
                            en: { videoId: 'SGfT09xM1k0' },
                            hi: { videoId: 'KOtS5Qd0kYY' },
                          },
                        })
                      }
                      iconLeft={<Icon type="VideoOutlined" size={16} />}
                      intent="primary"
                      outlined={true}
                      className="border-grays-60 mx-0  ml-[38] h-4 w-[120] rounded-xl px-1 py-[1.5]"
                      textStyle=" text-xs font-[Graphie-SemiBold] text-grays-60  "
                      eventName="click_action"
                      eventPayload={{
                        item_name: 'add_customer_verify_otp_watch_video',
                      }}
                    >
                      {i18n.t('customerDetails.watch')}
                    </Button>
                  </Stack>
                )}
                {!isExperianEnabled && (
                  <>
                    <B2 className=" mt-5 ">
                      {i18n.t('verifyPhNum.agree')}
                      <SH2
                        className="text-primary-100"
                        onPress={() => {
                          // navigation.navigate('WebViewScreen', {
                          //   url:
                          //     APP_NAME !== MY_TEAMS_ADVISOR
                          //       ? TERMS_AND_CONDITIONS.zetAgentTermsOfUse
                          //       : TERMS_AND_CONDITIONS.advisorAgentTermsOfUse,
                          // })
                          navigation.navigate('TermsOfUse', {
                            isCustomer: true,
                            isExperian: isExperianEnabled,
                          })
                          publishEvent('click_action', {
                            item_name: 'add_customer_terms_of_use',
                          })
                        }}
                      >
                        {' '}
                        {i18n.t('verifyPhNum.terms')}
                      </SH2>
                      <B2>{i18n.t('verifyPhNum.and')}</B2>
                      <SH2
                        className="text-primary-100"
                        onPress={() => {
                          // navigation.navigate('WebViewScreen', {
                          //   url:
                          //     APP_NAME !== MY_TEAMS_ADVISOR
                          //       ? TERMS_AND_CONDITIONS.zetAgentPrivacyPolicy
                          //       : TERMS_AND_CONDITIONS.advisorAgentPrivacyPolicy,
                          // })
                          navigation.navigate('PrivacyPolicy', {
                            isCustomer: true,
                            isExperian: isExperianEnabled,
                          })
                          publishEvent('click_action', {
                            item_name: 'add_customer_privacy_policy',
                          })
                        }}
                      >
                        {' '}
                        {i18n.t('verifyPhNum.privacyPolicy')}.
                      </SH2>
                      <B2>
                        {i18n.t('verifyPhNum.agreeSuffix', {
                          continue:
                            locale === 'hi'
                              ? i18n.t('verifyPhNum.continue')
                              : '',
                        })}
                      </B2>
                    </B2>
                  </>
                )}
              </>
            )}
            {!isReconsentFlow && (
              <Stack className="mb-4 justify-end self-center">
                <XStack className="my-[8%] w-full items-center justify-center  ">
                  <Stack className="bg-grays-30 h-[1]   flex-grow"></Stack>
                  <B5 className="border-grays-30 rounded-[32] border px-2 py-1">
                    {i18n.t('knowCustomer.or')}
                  </B5>
                  <Stack className="bg-grays-30  h-[1] flex-grow"></Stack>
                </XStack>
                {isShared ? (
                  <Button
                    textOnly={
                      !(APP_NAME === MY_TEAMS_ADVISOR && Platform.OS === 'web')
                    }
                    className="w-full self-center"
                    iconLeft={
                      <Icon
                        type="SecureFilled"
                        color={colors.primary[50]}
                        className=" mr-2 "
                      />
                    }
                    textStyle="text-primary-100"
                    onPress={resetToOtpField}
                    loading={isExperimentsLoading}
                    eventName="click_action"
                    eventPayload={{
                      item_name: isReconsentFlow
                        ? 'brands_list_recommendation_reconsent_enter_otp_manually'
                        : 'add_customer_otp_manually',
                    }}
                  >
                    {i18n.t('otpInput.enter')}
                  </Button>
                ) : (
                  <Button
                    textOnly={APP_NAME !== MY_TEAMS_ADVISOR}
                    outlined={APP_NAME === MY_TEAMS_ADVISOR}
                    className="w-full self-center"
                    iconLeft={
                      (refetching && isFetching) ||
                      (refetchingReconsent && isFetchingReconsent) ? (
                        <ActivityIndicator
                          size="small"
                          color={colors.primary[100]}
                        />
                      ) : (
                        <Icon
                          type="Share"
                          color={colors.primary[100]}
                          className=" mr-2 "
                        />
                      )
                    }
                    textStyle="text-primary-100"
                    onPress={() =>
                      isReconsentFlow ? refetchReconsent() : refetch()
                    }
                    eventName="click_action"
                    eventPayload={{
                      item_name: isReconsentFlow
                        ? 'brands_list_recommendation_reconsent_share_otp'
                        : 'add_customer_share_otp',
                    }}
                  >
                    {i18n.t('customerDetails.shareOtp')}
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        </KeyBoardWrapper>
      </AddCustomerBackHandlerHOC>
      <WebShare />
    </LazyHOC>
  )
}
const styles = StyleSheet.create({
  codeFieldRoot: {
    marginTop: 38,
    marginHorizontal: 58,
    alignItems: 'center',
    marginBottom: 20,
  },
  cell: {
    width: 48,
    height: 48,
    fontSize: 16,
    fontWeight: '500',
    borderWidth: 2,
    borderColor: colors.grays[40],
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    backgroundColor: '#F9F9FB',
  },
  focusCell: {
    borderColor: colors.primary[50],
  },
})
export default VerifyCustomerOtp
